/**
 * PictView-Retold-Notifications
 *
 * The SHARED notification-preferences view. It reads its shape from the server: GET
 * <prefix>Comms/Notifications returns the events the app can notify about and the caller's
 * current email/SMS choice for each, so the form is built from the server's contract. A
 * toggle saves immediately with PUT <prefix>Comms/Notifications.
 *
 * Registered by the chassis and reached at /Notifications; an app opts in by turning on the
 * comms subsystem and adding a navigation link. When the server has no preference route the
 * view shows a plain "not available" message.
 *
 * Theme: every themed background is paired with a themed foreground, so the view reads in
 * light and dark and degrades to sane light fallbacks in an unthemed host.
 */

const libPictView = require('pict-view');

function escapeHTML(pText)
{
	return String(pText === null || typeof pText === 'undefined' ? '' : pText)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

class PictViewRetoldNotifications extends libPictView
{
	_apiPrefix() { return this.options.APIPrefix || '/1.0/'; }

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap && typeof this.pict.CSSMap.injectCSS === 'function') { this.pict.CSSMap.injectCSS(); }
		this._load();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: pType || 'info' }); }
	}

	_load()
	{
		if (typeof document === 'undefined' || typeof fetch !== 'function') { return Promise.resolve(null); }
		let tmpList = document.getElementById('Retold-Notifications-List');
		let tmpUnavailable = document.getElementById('Retold-Notifications-Unavailable');
		return fetch(this._apiPrefix() + 'Comms/Notifications', { credentials: 'include', headers: { 'Accept': 'application/json' } })
			.then((pResponse) => pResponse.json().then((pJSON) => ({ status: pResponse.status, json: pJSON })).catch(() => ({ status: pResponse.status, json: null })))
			.then((pResult) =>
			{
				if (pResult.status !== 200 || !pResult.json)
				{
					if (tmpList) { tmpList.hidden = true; }
					if (tmpUnavailable)
					{
						tmpUnavailable.hidden = false;
						tmpUnavailable.textContent = (pResult.status === 401)
							? 'Please sign in to manage your notifications.'
							: 'Notification preferences are not available for this application.';
					}
					return null;
				}
				this._data = pResult.json;
				if (tmpUnavailable) { tmpUnavailable.hidden = true; }
				if (tmpList) { tmpList.hidden = false; }
				this._renderList();
				return this._data;
			})
			.catch(() => null);
	}

	_renderList()
	{
		if (typeof document === 'undefined' || !this._data) { return; }
		let tmpList = document.getElementById('Retold-Notifications-List');
		if (!tmpList) { return; }
		let tmpEvents = this._data.Events || [];
		let tmpCanSMS = !!this._data.CanSMS;

		if (!tmpEvents.length)
		{
			tmpList.innerHTML = '<p class="retold-notifications-empty">This application has no notification types yet.</p>';
			return;
		}

		let tmpHTML = '<div class="retold-notifications-head"><span></span><span>Email</span><span>Text</span></div>';
		tmpEvents.forEach((pEvent) =>
		{
			tmpHTML += '<div class="retold-notifications-row">' +
				'<span class="retold-notifications-label">' + escapeHTML(pEvent.Label || pEvent.EventKey) + '</span>' +
				'<span class="retold-notifications-cell">' +
					'<input type="checkbox" data-event="' + escapeHTML(pEvent.EventKey) + '" data-channel="email"' + (pEvent.EmailEnabled ? ' checked' : '') + '>' +
				'</span>' +
				'<span class="retold-notifications-cell">' +
					'<input type="checkbox" data-event="' + escapeHTML(pEvent.EventKey) + '" data-channel="sms"' + (pEvent.SMSEnabled ? ' checked' : '') + (tmpCanSMS ? '' : ' disabled') + '>' +
				'</span>' +
				'</div>';
		});
		if (!tmpCanSMS)
		{
			tmpHTML += '<p class="retold-notifications-note">Text messages need a verified phone. Add and verify one under Security.</p>';
		}
		tmpList.innerHTML = tmpHTML;

		Array.prototype.forEach.call(tmpList.querySelectorAll('input[type="checkbox"]:not([disabled])'), (pInput) =>
		{
			pInput.addEventListener('change', () => this._save(pInput));
		});
	}

	_save(pInput)
	{
		let tmpEventKey = pInput.getAttribute('data-event');
		let tmpChannel = pInput.getAttribute('data-channel');
		let tmpPatch = { EventKey: tmpEventKey };
		if (tmpChannel === 'sms') { tmpPatch.SMSEnabled = pInput.checked; }
		else { tmpPatch.EmailEnabled = pInput.checked; }

		return fetch(this._apiPrefix() + 'Comms/Notifications',
			{
				method: 'PUT', credentials: 'include',
				headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
				body: JSON.stringify(tmpPatch)
			})
			.then((pResponse) => pResponse.json().then((pJSON) => ({ status: pResponse.status, json: pJSON })).catch(() => ({ status: pResponse.status, json: null })))
			.then((pResult) =>
			{
				if (pResult.status === 200) { this._toast('Notification preference saved.', 'success'); return pResult.json; }
				pInput.checked = !pInput.checked; // revert the toggle on failure
				this._toast((pResult.json && pResult.json.Error) || 'Could not save that preference.', 'error');
				return null;
			})
			.catch(() =>
			{
				pInput.checked = !pInput.checked;
				this._toast('Could not save that preference.', 'error');
				return null;
			});
	}
}

module.exports = PictViewRetoldNotifications;
module.exports.default_configuration =
{
	ViewIdentifier: 'Retold-Notifications',
	DefaultRenderable: 'Retold-Notifications',
	DefaultDestinationAddress: '#PRSP_Container',
	AutoRender: false,
	CSS: /*css*/ `
		.retold-notifications { padding: 24px; color: var(--theme-color-text-primary, #1f2430); }
		.retold-notifications-card { max-width: 640px; margin: 0 auto; padding: 24px 28px; border-radius: 10px;
			background: var(--theme-color-background-panel, #ffffff);
			color: var(--theme-color-text-primary, #1f2430);
			border: 1px solid var(--theme-color-border-default, #d6d9de); }
		.retold-notifications-title { font-size: 20px; margin: 0 0 4px 0; color: inherit; }
		.retold-notifications-hint { margin: 0 0 18px 0; font-size: 13px; color: var(--theme-color-text-muted, #6b7280); }
		.retold-notifications-unavailable { margin: 8px 0 0 0; font-size: 14px; color: var(--theme-color-text-secondary, #55606f); }
		.retold-notifications-head { display: grid; grid-template-columns: 1fr 64px 64px; align-items: center; gap: 8px;
			padding: 0 0 8px 0; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em;
			color: var(--theme-color-text-secondary, #55606f);
			border-bottom: 1px solid var(--theme-color-border-default, #d6d9de); }
		.retold-notifications-head span:nth-child(n+2) { text-align: center; }
		.retold-notifications-row { display: grid; grid-template-columns: 1fr 64px 64px; align-items: center; gap: 8px;
			padding: 12px 0; border-bottom: 1px solid var(--theme-color-border-default, #eceef1); }
		.retold-notifications-label { font-size: 14px; color: inherit; }
		.retold-notifications-cell { text-align: center; }
		.retold-notifications-cell input { width: 17px; height: 17px; accent-color: var(--theme-color-brand-primary, #3a6ea5); cursor: pointer; }
		.retold-notifications-cell input[disabled] { cursor: not-allowed; opacity: 0.4; }
		.retold-notifications-note, .retold-notifications-empty { margin: 14px 0 0 0; font-size: 13px; color: var(--theme-color-text-muted, #6b7280); }
	`,
	Templates:
	[
		{
			Hash: 'Retold-Notifications',
			Template: '<div class="retold-notifications"><div class="retold-notifications-card">' +
				'<h1 class="retold-notifications-title">Notifications</h1>' +
				'<p class="retold-notifications-hint">Choose how you want to hear about each kind of update. Changes save as you make them.</p>' +
				'<p id="Retold-Notifications-Unavailable" class="retold-notifications-unavailable" hidden></p>' +
				'<div id="Retold-Notifications-List" hidden></div>' +
				'</div></div>'
		}
	],
	Renderables:
	[
		{ RenderableHash: 'Retold-Notifications', TemplateHash: 'Retold-Notifications', ContentDestinationAddress: '#PRSP_Container', RenderMethod: 'replace' }
	]
};
